import { Fragment } from 'react';
import { useSortBy } from 'react-instantsearch';
import { Listbox, Transition } from '@headlessui/react';

import Sort from '@/icons/light/sort.svg';

import { flatSortItems } from '../flats-sort';

import type { FunctionComponent } from 'react';

type SortBySelectProps = {
  className?: string;
  /** Índice principal. Las réplicas se derivan de él en `flatSortItems`. */
  indexName: string;
  /**
   * `false` monta el widget pero no lo pinta. El orden de `?orden=` de la URL
   * se aplica igual — mismo criterio que los filtros de precio/tamaño, que
   * siempre están montados aunque el tenant no muestre el control.
   */
  show?: boolean;
};

export const SortBySelect: FunctionComponent<SortBySelectProps> = ({
  className = '',
  indexName,
  show = true,
}) => {
  const { currentRefinement, options, refine } = useSortBy({
    items: flatSortItems(indexName),
  });

  if (!show) return null;

  // Sin refinamiento (o en el índice principal) el chip mantiene el rótulo
  // "Ordenar" del Figma; al elegir criterio pasa a mostrarlo.
  const current = options.find((o) => o.value === currentRefinement);
  const isDefault = !current || current.value === indexName;

  return (
    <div className={`text-body text-body-s relative ${className}`}>
      <Listbox value={currentRefinement} onChange={refine}>
        {({ open }) => (
          <>
            <Listbox.Button
              aria-label="Ordenar resultados"
              className="border-input-border text-text-primary inline-flex items-center gap-2 rounded-full border px-4 py-2"
            >
              <span className="block truncate">
                {isDefault ? 'Ordenar' : current?.label}
              </span>
              <Sort
                className={`h-4 w-4 transition ${open ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </Listbox.Button>
            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              {/* `anchor` portaliza el panel igual que en CustomSelect: la
                  barra de filtros tiene `overflow-x-auto` en mobile y si no lo
                  recortaría. Al portalizar se pierde la tipografía heredada,
                  de ahí que se declare aquí otra vez. */}
              <Listbox.Options
                anchor={{ to: 'bottom start', gap: 4 }}
                className="text-body text-body-s rounded-custom border-neutrals-100 bg-bg-neutrals-0 shadow-light-s z-20 w-64 overflow-hidden border"
              >
                {options.map((option) => (
                  <Listbox.Option
                    key={option.value}
                    value={option.value}
                    className="justify-left border-neutrals-50 text-text-neutrals-500 hover:bg-neutrals-50 flex cursor-pointer gap-4 border-b py-3 pr-2 pl-5 last:border-0"
                  >
                    {({ selected }) => (
                      <span
                        className={`block truncate ${
                          selected
                            ? 'text-text-neutrals-900 font-bold'
                            : 'font-normal'
                        }`}
                      >
                        {option.label}
                      </span>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </>
        )}
      </Listbox>
    </div>
  );
};

SortBySelect.displayName = 'SortBySelect';

export default SortBySelect;
